import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import Axios from 'axios';
import anime from 'animejs';

import Suggestions from './Suggestions';

import { isFocusable } from '../../../helpers';
import { breakpoints } from '../../../helpers/constants';

const Panel = ({
  isOpen,
  onClose,
  endpoint,
  suggestionsEndpoint,
  searchQueryParam,
  placeholder,
  submitLabel,
  closeLabel,
  suggestionsTitle
}) => {
  const panelRef = useRef(null);
  const panelBoxRef = useRef(null);
  const inputRef = useRef(null);
  const inputId = useMemo(() => uuidv4(), []);
  const [keyword, setKeyword] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const ignoreUtilFocusChanges = useRef(null);
  const lastFocus = useRef(null);

  const breakpoint = useMemo(
    () => window.matchMedia(`(min-width: ${breakpoints.tabletLandscape}px)`),
    []
  );

  const isFirstRender = useRef(true);

  const animatePanel = useCallback(
    ({ direction = 'normal' }) => {
      const panel = panelRef.current;
      const panelBox = panelBoxRef.current;

      const timeline = anime.timeline({
        easing: 'easeInOutQuad',
        direction,
        begin: () => {
          if (direction === 'normal') {
            panel.style.visibility = 'visible';
          }
        },
        complete: () => {
          if (direction === 'reverse') {
            panel.style.visibility = 'hidden';
            setKeyword('');
          } else {
            focusFirstDescendant(panelBox);
            inputRef.current.click();
          }
        }
      });

      timeline.add({
        targets: panel,
        opacity: [0, 1],
        duration: breakpoint.matches ? 150 : 0
      });

      if (breakpoint.matches) {
        timeline.add({
          targets: panelBox,
          translateX: ['100%', '0%'],
          duration: 400
        });
      } else {
        timeline.add({
          targets: panelBox,
          translateY: ['100%', '0%'],
          duration: 400
        });
      }
    },
    [breakpoint.matches]
  );

  const toggle = useCallback(() => {
    if (isOpen) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${window.scrollY}px`;
      animatePanel({});
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = null;
      document.body.style.top = null;
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
      animatePanel({ direction: 'reverse' });
    }
  }, [animatePanel, isOpen]);

  const focusFirstDescendant = useCallback(
    element => {
      for (let i = 0; i < element.childNodes.length; i++) {
        const child = element.childNodes[i];
        if (attemptFocus(child) || focusFirstDescendant(child)) {
          return true;
        }
      }
      return false;
    },
    [attemptFocus]
  );

  const focusLastDescendant = useCallback(
    element => {
      for (let i = element.childNodes.length - 1; i >= 0; i--) {
        const child = element.childNodes[i];
        if (attemptFocus(child) || focusLastDescendant(child)) {
          return true;
        }
      }
      return false;
    },
    [attemptFocus]
  );

  const attemptFocus = useCallback(element => {
    if (!isFocusable(element)) {
      return false;
    }

    ignoreUtilFocusChanges.current = true;
    try {
      element.focus();
    } catch (e) {
      return;
    }
    ignoreUtilFocusChanges.current = false;
    return document.activeElement === element;
  }, []);

  // Only toggle when isOpen changes (not on initial render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    toggle();
  }, [isOpen, toggle]);

  // Handle breakpoint changes without calling toggle
  useEffect(() => {
    const listener = () => {
      if (panelRef.current) {
        panelRef.current.style = null;
      }
      if (panelBoxRef.current) {
        panelBoxRef.current.style = null;
      }
    };

    breakpoint.addListener(listener);
    return () => breakpoint.removeListener(listener);
  }, [breakpoint]);

  useEffect(() => {
    const handleFocus = e => {
      if (ignoreUtilFocusChanges.current) return;

      if (panelBoxRef.current.contains(e.target)) {
        lastFocus.current = e.target;
      } else {
        focusFirstDescendant(panelBoxRef.current);

        if (lastFocus.current === document.activeElement) {
          focusLastDescendant(panelBoxRef.current);
        }
        lastFocus.current = document.activeElement;
      }
    };

    document.addEventListener('focus', handleFocus, true);
    return () => {
      document.removeEventListener('focus', handleFocus, true);
    };
  }, [focusFirstDescendant, focusLastDescendant]);

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await Axios({
        method: 'GET',
        url: suggestionsEndpoint,
        params: {
          [searchQueryParam]: keyword
        }
      });

      setSuggestions(data);
    };

    if (keyword.length > 2) {
      fetchData();
    } else {
      setSuggestions([]);
    }
  }, [keyword, searchQueryParam, suggestionsEndpoint]);

  return (
    <div
      className={`header-search__panel ${
        isOpen ? 'header-search__panel--open' : ''
      }`}
      ref={panelRef}
    >
      <div
        className="header-search__box"
        ref={panelBoxRef}
        onClick={e => e.stopPropagation()}
      >
        <form
          action={endpoint}
          method="GET"
          className="header-search__form"
          autoComplete="off"
        >
          <label htmlFor={inputId}>{placeholder}</label>
          <input
            ref={inputRef}
            type="search"
            name={searchQueryParam}
            id={inputId}
            placeholder={placeholder}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
          />
          <button
            type="submit"
            className="header-search__submit"
            aria-label={submitLabel}
          >
            <svg>
              <title>search</title>
              <use xlinkHref="#search" />
            </svg>
          </button>
          <button
            type="button"
            className="header-search__close"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <svg>
              <title>Close Search</title>
              <use xlinkHref="#close" />
            </svg>
          </button>
        </form>
        <Suggestions
          title={suggestionsTitle}
          suggestions={suggestions}
          endpoint={endpoint}
          searchQueryParam={searchQueryParam}
        />
      </div>
    </div>
  );
};

Panel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  submitLabel: PropTypes.string.isRequired,
  closeLabel: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  searchQueryParam: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  suggestionsTitle: PropTypes.string.isRequired,
  suggestionsEndpoint: PropTypes.string.isRequired
};

export default Panel;
